import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { reportsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  FileSpreadsheet,
  FileText,
  Download,
  Calendar,
  DollarSign,
  Users,
  Home,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const LandlordReports = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('rent-roll');

  // Rent roll state
  const [rentRoll, setRentRoll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [rrLoading, setRRLoading] = useState(false);

  // Payment history state
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [phLoading, setPHLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'rent-roll') loadRentRoll();
    else loadPaymentHistory();
  }, [activeTab]);

  const loadRentRoll = async () => {
    setRRLoading(true);
    try {
      const res = await reportsAPI.getRentRoll(selectedMonth);
      setRentRoll(res.data);
    } catch {
      toast.error('Failed to load rent roll');
    } finally {
      setRRLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    setPHLoading(true);
    try {
      const res = await reportsAPI.getPaymentHistory(startDate, endDate);
      setPaymentHistory(res.data);
    } catch {
      toast.error('Failed to load payment history');
    } finally {
      setPHLoading(false);
    }
  };

  const handleExport = async (type, format) => {
    try {
      let res;
      let filename;
      if (type === 'rent-roll') {
        res = await reportsAPI.exportRentRoll(selectedMonth, format);
        filename = `rent-roll-${selectedMonth}.${format}`;
      } else {
        res = await reportsAPI.exportPaymentHistory(startDate, endDate, format);
        filename = `payment-history-${startDate}-to-${endDate}.${format}`;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch {
      toast.error('Export failed');
    }
  };

  const changeMonth = (delta) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const newMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(newMonth);
  };

  useEffect(() => {
    if (activeTab === 'rent-roll') loadRentRoll();
  }, [selectedMonth]);

  const getStatusBadge = (status) => {
    const map = {
      PAID: { cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle },
      PENDING: { cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
      OVERDUE: { cls: 'bg-red-500/10 text-red-400 border-red-500/20', icon: AlertCircle },
      'NO PAYMENT': { cls: 'bg-gray-500/10 text-gray-400 border-gray-500/20', icon: Clock },
    };
    const cfg = map[status] || map['NO PAYMENT'];
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs border ${cfg.cls}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatMonth = (m) => {
    const [y, mo] = m.split('-');
    return new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <DashboardLayout type="landlord" title={t('reports.title')}>
      <div className="space-y-6" data-testid="landlord-reports">
        <div>
          <h2 className="font-serif text-2xl mb-1">Reports & Export</h2>
          <p className="text-muted-foreground">View and export your property financial data</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'rent-roll' ? 'default' : 'outline'}
            className={activeTab === 'rent-roll' ? 'bg-gold text-white hover:bg-gold-light' : 'border-border'}
            onClick={() => setActiveTab('rent-roll')}
            data-testid="tab-rent-roll"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Monthly Rent Roll
          </Button>
          <Button
            variant={activeTab === 'payment-history' ? 'default' : 'outline'}
            className={activeTab === 'payment-history' ? 'bg-gold text-white hover:bg-gold-light' : 'border-border'}
            onClick={() => setActiveTab('payment-history')}
            data-testid="tab-payment-history"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Payment History
          </Button>
        </div>

        {/* RENT ROLL */}
        {activeTab === 'rent-roll' && (
          <div className="space-y-4">
            {/* Controls */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)} data-testid="prev-month-btn">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="font-serif text-lg min-w-[160px] text-center" data-testid="current-month">{formatMonth(selectedMonth)}</span>
                    <Button variant="ghost" size="icon" onClick={() => changeMonth(1)} data-testid="next-month-btn">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="border-border" onClick={() => handleExport('rent-roll', 'csv')} data-testid="export-csv-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" className="border-border" onClick={() => handleExport('rent-roll', 'pdf')} data-testid="export-pdf-btn">
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            {rentRoll && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Rent</p>
                    <p className="text-2xl font-semibold">${rentRoll.summary.total_rent.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Collected</p>
                    <p className="text-2xl font-semibold text-emerald-400">${rentRoll.summary.total_collected.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pending</p>
                    <p className="text-2xl font-semibold text-amber-400">${rentRoll.summary.total_pending.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Overdue</p>
                    <p className="text-2xl font-semibold text-red-400">${rentRoll.summary.total_overdue.toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Rent Roll Table */}
            {rrLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-card animate-pulse" />)}</div>
            ) : rentRoll && rentRoll.rows.length > 0 ? (
              <Card className="bg-card border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rent-roll-table">
                    <thead>
                      <tr className="border-b border-border bg-white">
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Property</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Unit</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Tenant</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider text-muted-foreground">Rent</th>
                        <th className="text-center p-3 text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider text-muted-foreground">Late Fee</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rentRoll.rows.map((r, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]" data-testid={`rent-roll-row-${i}`}>
                          <td className="p-3 flex items-center gap-2"><Home className="w-4 h-4 text-muted-foreground" />{r.property_name}</td>
                          <td className="p-3">{r.unit_number}</td>
                          <td className="p-3">{r.tenant_name}</td>
                          <td className="p-3 text-right font-medium">${r.monthly_rent.toLocaleString()}</td>
                          <td className="p-3 text-center">{getStatusBadge(r.payment_status)}</td>
                          <td className="p-3 text-right">{r.late_fee > 0 ? <span className="text-red-400">${r.late_fee.toFixed(2)}</span> : '—'}</td>
                          <td className="p-3 text-muted-foreground">{r.paid_date || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <FileSpreadsheet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No lease data for this month</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* PAYMENT HISTORY */}
        {activeTab === 'payment-history' && (
          <div className="space-y-4">
            {/* Controls */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-muted-foreground">From:</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-white border-[#e5e7eb] w-[160px]"
                        data-testid="start-date-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-muted-foreground">To:</label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-white border-[#e5e7eb] w-[160px]"
                        data-testid="end-date-input"
                      />
                    </div>
                    <Button size="sm" className="bg-gold text-white hover:bg-gold-light" onClick={loadPaymentHistory} data-testid="apply-dates-btn">
                      Apply
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="border-border" onClick={() => handleExport('payment-history', 'csv')} data-testid="export-ph-csv-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" className="border-border" onClick={() => handleExport('payment-history', 'pdf')} data-testid="export-ph-pdf-btn">
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            {paymentHistory && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Amount</p>
                    <p className="text-2xl font-semibold">${paymentHistory.summary.total_amount.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Collected</p>
                    <p className="text-2xl font-semibold text-emerald-400">${paymentHistory.summary.total_collected.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Outstanding</p>
                    <p className="text-2xl font-semibold text-red-400">${paymentHistory.summary.total_outstanding.toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Payment History Table */}
            {phLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-card animate-pulse" />)}</div>
            ) : paymentHistory && paymentHistory.rows.length > 0 ? (
              <Card className="bg-card border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="payment-history-table">
                    <thead>
                      <tr className="border-b border-border bg-white">
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Due Date</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Paid</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Property</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Unit</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground">Tenant</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider text-muted-foreground">Amount</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider text-muted-foreground">Late Fee</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider text-muted-foreground">Total</th>
                        <th className="text-center p-3 text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.rows.map((r, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]" data-testid={`ph-row-${i}`}>
                          <td className="p-3">{r.due_date}</td>
                          <td className="p-3 text-muted-foreground">{r.paid_date || '—'}</td>
                          <td className="p-3">{r.property_name}</td>
                          <td className="p-3">{r.unit_number}</td>
                          <td className="p-3">{r.tenant_name}</td>
                          <td className="p-3 text-right">${r.amount.toLocaleString()}</td>
                          <td className="p-3 text-right">{r.late_fee > 0 ? <span className="text-red-400">${r.late_fee.toFixed(2)}</span> : '—'}</td>
                          <td className="p-3 text-right font-medium">${r.total.toLocaleString()}</td>
                          <td className="p-3 text-center">{getStatusBadge(r.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <DollarSign className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No payments in this date range</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LandlordReports;
